import { useEffect, useState } from 'react'
import { logout as logoutEndpoint } from '../../api/applicationApi'
import { QueryParameterNames, LogoutActions, ApplicationPaths } from './ApiAuthorizationConstants';
import { toast } from 'react-toastify';
import { logError } from '../../utilities';
import LoadingIcon from '../LoadingIcon';

// The main responsibility of this component is to handle the user's logout process.
// This is the starting point for the logout process, which is usually initiated when a
// user clicks on the logout button on the LoginMenu component.
export function Logout(props: { message?: string, isReady?: boolean, authenticated?: boolean, setAuthenticated: (a: boolean) => void, action: string }){
  const [isReady, setIsReady] = useState<boolean>(false);
  const [message, setMessage] = useState<string | null>(null);
  const [isLoggingOut, setIsLoggingOut] = useState<boolean>(false);

  const notifyUserOfError = (err: string) => toast(err, { type: 'error' });
  const notifyUserOfSuccess = (msg: string) => toast(msg, { type: 'success' });

  const navigateToReturnUrl = (returnUrl: string) => {
    return window.location.replace(returnUrl);
  };

  const logout = async(returnUrl: string) => {
    try {
      setIsLoggingOut(true);
      await logoutEndpoint();
      props.setAuthenticated(false);
      notifyUserOfSuccess('You successfully logged out!');
      navigateToReturnUrl(returnUrl);
    }
    catch (error: any) {
      if (error instanceof Error) {
          notifyUserOfError(error.message);
      }

      logError(error);
    }
    finally{
      setIsLoggingOut(false);
    }
  };

  const populateAuthenticationState = async() => {
    setIsReady(true);
  };

  const getReturnUrl = (state?: { returnUrl: string }): string => {
    const params = new URLSearchParams(window.location.search);
    const fromQuery = params.get(QueryParameterNames.ReturnUrl);
    if (fromQuery && !fromQuery.startsWith(`${window.location.origin}/`)) {
      // This is an extra check to prevent open redirects.
      throw new Error("Invalid return url. The return url needs to have the same origin as the current page.")
    }
    return (state && state.returnUrl) ||
      fromQuery ||
      `${window.location.origin}${ApplicationPaths.LoggedOut}`;
  };

  useEffect(() => {
    const action = props.action;
    switch (action) {
      case LogoutActions.Logout:
        if (!!window.history.state.usr.local) {
          logout(getReturnUrl());
        } else {
          // This prevents regular links to <app>/authentication/logout from triggering a logout
          setIsReady(true);
          setMessage("The logout was not initiated from within the page.");
        }
        break;
      case LogoutActions.LoggedOut:
        setIsReady(true);
        setMessage("You successfully logged out!");
        break;
      default:
        throw new Error(`Invalid action '${action}'`);
    }

    populateAuthenticationState();
  }, [ props.action ]);

  if (!isReady || isLoggingOut) {
    return <LoadingIcon />
  }
  if (!!message) {
    return (<div>{message}</div>);
  } else {
    switch (props.action) {
      case LogoutActions.Logout:
        return (<div>Processing logout</div>);
      case LogoutActions.LogoutCallback:
        return (<div>Processing logout callback</div>);
      case LogoutActions.LoggedOut:
        return (<div>{message}</div>);
      default:
        throw new Error(`Invalid action '${props.action}'`);
    }
  }
}